import { useState, useRef, useEffect } from 'react';

export default function ChatBox({ messages, onSendMessage, isOpen, onClose, userName }) {
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      onSendMessage(messageText.trim());
      setMessageText('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-[#1c1c1e] border-l border-white/10 flex flex-col z-[60] animate-slide-in">
      {/* Header */}
      <div className="bg-[#2c2c2e] border-b border-white/10 px-6 py-4 flex justify-between items-center">
        <div>
          <h2 className="text-white text-lg font-semibold">Chat</h2>
          <p className="text-[#8e8e93] text-xs">Peer-to-peer encrypted</p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
          title="Close chat"
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-[#2c2c2e] rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-white/60 text-sm">No messages yet</p>
            <p className="text-white/40 text-xs mt-1">Start a conversation</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isOwnMessage = msg.sender === userName;
              return (
                <div
                  key={index}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                    {!isOwnMessage && (
                      <span className="text-[#8e8e93] text-xs font-medium px-2">
                        {msg.sender}
                      </span>
                    )}
                    <div
                      className={`px-4 py-2.5 rounded-2xl ${
                        isOwnMessage
                          ? 'bg-gradient-to-r from-[#06c] to-[#0055d4] text-white'
                          : 'bg-[#2c2c2e] text-white'
                      }`}
                    >
                      <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                    </div>
                    <span className="text-[#8e8e93] text-[10px] px-2">
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <form onSubmit={handleSend} className="bg-[#2c2c2e] border-t border-white/10 px-6 py-4">
        <div className="flex gap-2 items-end">
          <div className="flex-1 bg-[#1c1c1e] rounded-2xl border border-white/10 flex items-center px-4 py-2">
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-transparent text-white placeholder-[#8e8e93] outline-none text-sm"
              maxLength={500}
            />
          </div>
          <button
            type="submit"
            disabled={!messageText.trim()}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              messageText.trim()
                ? 'bg-gradient-to-r from-[#06c] to-[#0055d4] hover:shadow-lg hover:scale-105'
                : 'bg-white/10 cursor-not-allowed'
            }`}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <p className="text-[#8e8e93] text-[10px] mt-2 text-center">
          Messages are sent peer-to-peer and never stored
        </p>
      </form>
    </div>
  );
}
